import React, { useEffect, useState } from 'react';
import { ref, onValue, set, remove } from 'firebase/database';
import { db } from '../firebase';
import { PriceEntryForm } from './PriceEntryForm';
import { PriceGraph } from './PriceGraph';
import { ArtworkList } from './ArtworkList';
export type PriceEntry = {
  id: string;
  year: number;
  price: number;
};
type ArtworkData = {
  [key: string]: PriceEntry[];
};
export function ArtworkPriceTracker() {
  const [artworkData, setArtworkData] = useState<ArtworkData>(() => {
    // Load initial data from localStorage
    const savedData = localStorage.getItem('artworkData');
    return savedData ? JSON.parse(savedData) : {};
  });
  const [artworkName, setArtworkName] = useState<string>('');
  const [selectedArtwork, setSelectedArtwork] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  // Load data from Firebase on initial render
  useEffect(() => {
    const artworksRef = ref(db, 'artworks');
    const unsubscribe = onValue(artworksRef, snapshot => {
      const data = snapshot.val();
      if (data) {
        setArtworkData(data);
        // Select the first artwork if none is selected
        if (!selectedArtwork && Object.keys(data).length > 0) {
          setSelectedArtwork(Object.keys(data)[0]);
        }
      } else {
        // If no data in Firebase, initialize with empty object
        setArtworkData({});
      }
    }, error => {
      console.error('Firebase data loading error:', error);
      // If Firebase fails, fallback to localStorage data
      const savedData = localStorage.getItem('artworkData');
      if (savedData) {
        setArtworkData(JSON.parse(savedData));
      }
    });
    return () => unsubscribe();
  }, []);
  // Save data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('artworkData', JSON.stringify(artworkData));
  }, [artworkData]);
  const handleAddArtwork = async () => {
    if (!artworkName.trim()) return;
    if (artworkData[artworkName]) {
      alert('An artwork with this name already exists');
      return;
    }
    setIsAdding(true);
    try {
      // Sanitize the artwork name to be safe for Firebase paths
      const sanitizedName = artworkName.replace(/[.#$[\]/]/g, '_');
      const newArtworkRef = ref(db, `artworks/${sanitizedName}`);
      console.log('Attempting to add artwork:', sanitizedName);
      console.log('Database reference path:', newArtworkRef.toString());
      // Set initial empty array with null check
      await set(newArtworkRef, []);
      // Update local state
      setSelectedArtwork(sanitizedName);
      setArtworkName('');
      console.log('Successfully added artwork:', sanitizedName);
    } catch (error: any) {
      console.error('Detailed error adding artwork:', {
        error: error,
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      alert(`Failed to add artwork. Error: ${error.message}. Please try again or contact support if the problem persists.`);
    } finally {
      setIsAdding(false);
    }
  };
  const deleteArtwork = (name: string) => {
    if (!window.confirm(`Are you sure you want to delete ${name}?`)) return;
    const artworkRef = ref(db, `artworks/${name}`);
    remove(artworkRef).then(() => {
      if (selectedArtwork === name) {
        const remaining = Object.keys(artworkData).filter(k => k !== name);
        setSelectedArtwork(remaining.length > 0 ? remaining[0] : null);
      }
    });
  };
  const addPriceEntry = (entry: PriceEntry) => {
    if (!selectedArtwork) return;
    const currentEntries = artworkData[selectedArtwork] || [];
    const newEntries = [...currentEntries, entry].sort((a, b) => a.year - b.year);
    // If this is the first entry, use it as the baseline (100%)
    if (currentEntries.length === 0) {
      const artworkRef = ref(db, `artworks/${selectedArtwork}`);
      set(artworkRef, [{
        ...entry,
        price: 100
      }]);
      return;
    }
    // Calculate percentage relative to the first entry
    const firstPrice = newEntries[0].price;
    const updatedEntries = newEntries.map(e => ({
      ...e,
      price: e === newEntries[0] ? 100 : e.price / firstPrice * 100
    }));
    const artworkRef = ref(db, `artworks/${selectedArtwork}`);
    set(artworkRef, updatedEntries);
  };
  const removePriceEntry = (id: string) => {
    if (!selectedArtwork) return;
    const updatedEntries = artworkData[selectedArtwork].filter(entry => entry.id !== id);
    const artworkRef = ref(db, `artworks/${selectedArtwork}`);
    set(artworkRef, updatedEntries);
  };
  const currentEntries = selectedArtwork ? artworkData[selectedArtwork] || [] : [];
  return <div className="max-w-7xl mx-auto" data-id="element-14">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6" data-id="element-15">
        ARTWORK VALUE OVER TIME
      </h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-id="element-16">
        <div className="lg:col-span-2" data-id="element-17">
          <label htmlFor="artworkName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-18">
            Add New Artwork
          </label>
          <div className="flex gap-2" data-id="element-19">
            <input type="text" id="artworkName" value={artworkName} onChange={e => setArtworkName(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" placeholder="Enter artwork name" disabled={isAdding} data-id="element-20" />
            <button onClick={handleAddArtwork} disabled={isAdding || !artworkName.trim()} className={`px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed`} data-id="element-21">
              {isAdding ? 'Adding...' : 'Add'}
            </button>
          </div>
        </div>
        <div className="lg:col-span-1" data-id="element-22">
          <h3 className="text-sm font-medium text-gray-700 mb-1" data-id="element-23">
            Your Artworks
          </h3>
          <ArtworkList artworks={Object.keys(artworkData)} selectedArtwork={selectedArtwork} onSelect={setSelectedArtwork} onDelete={deleteArtwork} data-id="element-24" />
        </div>
      </div>
      {selectedArtwork && <div className="space-y-6" data-id="element-25">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg" data-id="element-26">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-27">
              <PriceEntryForm onAddEntry={addPriceEntry} data-id="element-28" />
              <div data-id="element-29">
                <h3 className="text-lg font-medium text-gray-800 mb-3" data-id="element-30">
                  Value History
                </h3>
                {currentEntries.length === 0 ? <p className="text-gray-500" data-id="element-31">No value data added yet.</p> : <div className="overflow-auto max-h-[280px]" data-id="element-32">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-33">
                      <thead className="bg-gray-50" data-id="element-34">
                        <tr data-id="element-35">
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-36">
                            Year
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-37">
                            Value Change
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-38">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-39">
                        {currentEntries.map(entry => <tr key={entry.id} data-id="element-40">
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-41">
                              {entry.year}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-42">
                              {entry.price === 100 ? 'Baseline (100%)' : `${entry.price.toFixed(1)}%`}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm" data-id="element-43">
                              <button onClick={() => removePriceEntry(entry.id)} className="text-red-600 hover:text-red-800" data-id="element-44">
                                Remove
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>}
              </div>
            </div>
          </div>
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg" data-id="element-45">
            <h3 className="text-lg font-medium text-gray-800 mb-3" data-id="element-46">
              Value Growth Trend
            </h3>
            <PriceGraph data={currentEntries} artworkName={selectedArtwork} data-id="element-47" />
          </div>
        </div>}
    </div>;
}